import { TablePagination } from '@/components/ui/table';
import { messages } from '@/i18n';
import type { CollectionRow } from '@/types/collection';
import { CollectionCard, SectionError, SectionSkeleton } from './collectionsComponents';

/**
 * The list itself: skeleton, error, empty state, cards, and the pager.
 * Purely presentational: the ready and collected pages each own their own
 * query and pass the result in, since a row that moves between caches
 * (docs/driver-collections-split-plan.md §3.1 blocker #1) is a mutation
 * concern, not a rendering one.
 */
export function CollectionsSection({
  items,
  isLoading,
  isError,
  emptyMessage,
  errorMessage,
  onRetry,
  onAction,
  cardRefFor,
  pagination,
}: {
  items: CollectionRow[];
  isLoading: boolean;
  isError: boolean;
  emptyMessage: string;
  errorMessage: string;
  onRetry: () => void;
  onAction: (collection: CollectionRow) => void;
  cardRefFor?: (id: string) => (node: HTMLDivElement | null) => void;
  pagination?: {
    skip: number;
    total: number;
    pageSize: number;
    onSkipChange: (skip: number) => void;
  };
}) {
  const t = messages.driverCollections;

  if (isLoading) return <SectionSkeleton />;
  if (isError) return <SectionError message={errorMessage} onRetry={onRetry} />;
  if (items.length === 0) return <p className="text-muted-foreground text-sm">{emptyMessage}</p>;

  return (
    <>
      <div className="space-y-3">
        {items.map((collection) => (
          <CollectionCard
            key={collection.id}
            collection={collection}
            cardRef={cardRefFor?.(collection.id)}
            onAction={onAction}
          />
        ))}
      </div>
      {pagination && pagination.total > 0 ? (
        <div className="mt-4">
          <TablePagination
            summary={`${pagination.skip + 1}–${Math.min(pagination.skip + pagination.pageSize, pagination.total)} / ${pagination.total}`}
            previousLabel={t.previousPage}
            nextLabel={t.nextPage}
            canGoBack={pagination.skip > 0}
            canGoForward={pagination.skip + pagination.pageSize < pagination.total}
            onPrevious={() => pagination.onSkipChange(Math.max(0, pagination.skip - pagination.pageSize))}
            onNext={() => pagination.onSkipChange(pagination.skip + pagination.pageSize)}
          />
        </div>
      ) : null}
    </>
  );
}
